<template>
    <form :class="formClass">
        <Disclosure
            v-for="(filter, filterIdx) in filterState"
            v-slot="{ open }"
            :key="filterIdx"
            as="div"
            class="pb-4"
            :default-open="filter.selected.length > 0 || filterIdx === 0"
        >
            <div class="flow-root">
                <DisclosureButton class="w-full">
                    <div :class="disclosureButtonClass(open)">
                        <div :class="['self-stretch h-8 pb-2 justify-between items-center inline-flex', open ? ' border-b border-b-anthrazit-60':'']">
                            <div class="py-0.5 justify-start items-center gap-2 flex">
                                <div class="text-anthrazit-90 text-sm font-bold font-sans uppercase leading-tight tracking-wide">
                                    {{ filter.title }}
                                    <span
                                        v-if="filter.selected.length > 0"
                                        class="w-6 h-6 px-px py-[5px] leading-none bg-sand-80 rounded justify-center items-center inline-flex"
                                    >
                                        <span class="w-[22px] h-3.5 text-center text-anthrazit text-sm font-bold font-sans">{{ filter.selected.length }}</span>
                                    </span>
                                </div>
                            </div>
                            <span class="ml-6 flex items-center">
                                <ChevronDownIcon v-if="!open" class="size-5" aria-hidden="true"/>
                                <ChevronUpIcon v-else class="size-5" aria-hidden="true"/>
                            </span>
                        </div>
                    </div>
                </DisclosureButton>
            </div>
            <DisclosurePanel>
                <div class="bg-sand-20 px-3 pb-5 pt-1">
                    <template
                        v-for="(option, optionIdx) in filter.options"
                        :key="optionIdx"
                    >
                        <button
                            v-if="option"
                            v-show="optionIdx < filter.visibleItems"
                            class="flex w-full p-1 cursor-pointer focus-visible:border-gutex-rot"
                            @click.prevent="updateFilter(filter, option)"
                        >
                            <InputElement
                                :type="filterType(filter)"
                                :checked="filter.selected.includes(option)"
                                :label="option"
                            />
                        </button>
                    </template>
                    <div v-if="filter.visibleItems < filter.options.length" class="mt-4 flex w-full justify-end">
                        <a
                            class="rounded grid grid-flow-col cursor-pointer text-anthrazit-90 text-sm font-bold font-sans leading-1"
                            @click.prevent="showMoreItems(filter)"
                        >
                            {{ i18n.showMore }}
                            <ChevronDownIcon
                                class="relative size-5 shrink-0 text-anthrazit-90"
                                aria-hidden="true"
                            />
                        </a>
                    </div>
                </div>
            </DisclosurePanel>
        </Disclosure>
        <div
            v-if="isFilterSelected"
            class="lg:flex flex-row hidden px-4 pt-2 gap-1"
        >
            <div class="w-4 h-4 relative">
                <SvgIcon name="refresh" :small="true"/>
            </div>
            <a
                class="cursor-pointer text-anthrazit-90 text-sm font-bold font-sans leading-4"
                @click.prevent="resetFilter">{{ i18n.reset }}</a>
        </div>
        <div v-if="isFilterSelected" class="grid my-10 gap-4 lg:hidden grid-cols-1 sm:grid-cols-2 sm:gap-8 items-center">
            <button class="button-sm secondary" @click.prevent="resetFilter">{{ i18n.reset }}</button>
            <button class="button-sm" @click.prevent="apply">{{ i18n.apply }}</button>
        </div>
    </form>
</template>
<script>
import {
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
} from "@headlessui/vue"
import {ChevronUpIcon, ChevronDownIcon} from "@heroicons/vue/20/solid"
import InputElement from "./DatasetFilter/InputElement.vue"
import SvgIcon from "@components/SvgIcon.vue"

export default {
    name: "DatasetFilter",

    components: {
        SvgIcon,
        Disclosure,
        DisclosureButton,
        DisclosurePanel,
        ChevronUpIcon,
        ChevronDownIcon,
        InputElement,
    },

    props: {
        filters: {
            type: Array,
            required: true,
        },
        formClass: {
            type: String,
            default: "hidden xl:block"
        },
        i18n: {
            type: Object,
            default: () => {
                return {
                    showMore: "Show more",
                    reset: "Reset",
                    apply: "Apply",
                }
            }
        }
    },

    inject: ['setActive', 'dsPages'],

    emits: ["update:filter-fields", "update:filter-apply"],

    data: function () {
        return {
            mobileFiltersOpen: false,
            filterState: [],
        }
    },

    computed: {
        isFilterSelected() {
            return this.filterState.some(filter => filter.selected.length > 0)
        }
    },

    created() {
        this.filterState = this.clone(this.filters)
        this.restoreFilterFromUrl()
    },

    methods: {
        showMoreItems(filter) {
            filter.visibleItems += filter.visibleItems
        },

        restoreFilterFromUrl() {
            const params = new URLSearchParams(window.location.search)
            if (params.has("f")) {
                const filters = JSON.parse(params.get("f"))
                this.filterState.forEach(filter => {
                    if (filters[filter.name]) {
                        filter.selected = filters[filter.name].split(",")
                    }
                })
                this.updateState()
                const page = JSON.parse(params.get("p"))
                setTimeout(() => {
                    if(page && this.dsPages.includes(page)){
                        this.setActive(page)
                    }
                }, 100)
            }
        },

        clone(obj) {
            return JSON.parse(JSON.stringify(obj || {}))
        },

        fetchFilterState() {
            const fields = {}
            this.filterState.forEach(filter => {
                fields[filter.name] = function (value) {
                    if (filter.selected.length === 0) {
                        return true
                    }
                    if(Array.isArray(value)){
                        return filter.selected.some(item => value.includes(item))
                    }
                    return filter.selected.includes(value)
                }.bind({filter: filter})
            })
            return fields
        },

        updateState() {
            this.$emit("update:filter-fields", this.fetchFilterState())
        },

        updateFilter(filter, option) {
            if (this.filterType(filter) === "radio") {
                filter.selected = [option]
            } else if (!filter.selected.includes(option)) {
                filter.selected.push(option)
            } else {
                filter.selected = filter.selected.filter(item => item !== option)
            }
            this.updateState()
            this.saveFilterParameters()
        },

        apply() {
            this.updateState()
            this.$emit("update:filter-apply", this.fetchFilterState())
        },

        resetFilter() {
            this.filterState = this.clone(this.filters)
            this.resetParameter()
            this.updateState()
        },

        resetParameter() {
            const params = new URLSearchParams(window.location.search)
            params.delete("f")
            this.replaceHistoryState(params)
        },

        saveFilterParameters() {
            const params = new URLSearchParams(window.location.search)
            const filters = {}
            params.delete("f")
            this.filterState.forEach(filter => {
                if (filter.selected.length > 0) {
                    filters[filter.name] = filter.selected.join(",")
                }
            })
            params.append("f", JSON.stringify(filters))
            this.replaceHistoryState(params)
        },

        replaceHistoryState(params) {
            window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`)
        },

        filterType(filter) {
            return filter.type === "multiselect" ? "checkbox" : "radio"
        },

        disclosureButtonClass(opened) {
            return opened ? "w-full h-14 px-4 pt-4 pb-2 bg-[#f6f5f3] rounded-t-lg flex-col justify-start items-start inline-flex"
                : "w-full h-14 px-4 pt-4 pb-2 bg-[#f6f5f3] rounded-lg flex-col justify-start items-start inline-flex"
        }
    }
}
</script>
